import { useMonetization } from "#webapp/src/hooks/useMonetization";
import { useQuery, useSubscription } from "@apollo/client";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
import { getError, getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { webappDefaultWindowName } from "@clockwise/web-commons/src/constants/route.constants";
import { useCurrentTime } from "@clockwise/web-commons/src/hooks/useCurrentTime";
import { Card } from "@clockwise/web-commons/src/ui/card";
import { DefragRunFlowState } from "@clockwise/web-commons/src/util/event-history.util";
import { getUrlForFeature } from "@clockwise/web-commons/src/util/routes.util";
import { Skeleton } from "@material-ui/lab";
import { compact } from "lodash";
import React, { useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setPricingDialogOpen } from "../../state/actions/payments.actions";
import {
  lastAutopilotSuggestionsCardTimestamp,
  lastDefragCardTimestamp,
} from "../../state/local-storage";
import { IReduxState } from "../../state/reducers/root.reducer";
import { TrackingEvents, track } from "../../util/analytics.util";
import { AutopilotStatusCard } from "./AutopilotStatusCard";
import { AutopilotStatusFinished } from "./AutopilotStatusFinished";
import { AutopilotStatusUnstarted } from "./AutopilotStatusUnstarted";
import { AutopilotStatusDocument } from "./__generated__/AutopilotStatus.generated";
import { OrgFlowStateSubscriptionDocument } from "./__generated__/OrgFlowStateSubscription.generated";
import { AutopilotStatusTrialExpired } from "./autopilot-status-trial-expired";

type Props = {
  orgId: string;
  timeZone: string;
};

const ONE_HOUR = 60 * 60 * 1000;
const TWELVE_HOURS = 12 * ONE_HOUR;

export function AutopilotStatus({ orgId, timeZone }: Props) {
  const t = useContext(TranslationContext);
  const dispatch = useDispatch();
  const currentTime = useCurrentTime(ONE_HOUR);
  const { lastViewedFeedMillis } = useSelector((state: IReduxState) => state.chromeFeed);
  const { fastCrawlFinished } = useSelector((state: IReduxState) => state.onboarding);

  const activeView = useSelector((state: IReduxState) => state.webExtension.activeView);
  const { data, loading } = useQuery(AutopilotStatusDocument, {
    variables: { orgId, timeZone },
    pollInterval: 15 * 60 * 1000,
  });
  const org = getValue(data?.org, "Org");
  const flowState = getValue(org?.orgFlowStates)?.list.find(
    (flowState) => flowState.flowKey === "DefragRun",
  );
  useSubscription(OrgFlowStateSubscriptionDocument, {
    // Always defined because we only subscribe when we have a flow state.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
    variables: { flowStateRelayId: flowState?.id! },
    skip: !flowState,
  });

  const { canUserAccessFlexMeetings } = useMonetization();

  const badged = useMemo(() => {
    const summary = getValue(org?.tailoredDefragCardSummary);
    const lastDefragTime = parseInt(lastDefragCardTimestamp.get() || "0");
    const lastSuggestionTime = parseInt(lastAutopilotSuggestionsCardTimestamp.get() || "0");
    const showDefragCard =
      summary && summary.yourEventMoves.length > 0 && lastDefragTime < summary.defragTime;
    const knobCount = summary
      ? summary.knobs.autopilotMeetingKnobs.length +
        summary.knobs.inviteOrganizerKnobs.length +
        summary.knobs.lessenAutopilotConstraintKnobs.length
      : 0;
    const showAutopilotSuggestionsCard =
      summary && knobCount > 0 && lastSuggestionTime > summary.defragTime;
    const hasNotSeenDefrag = summary && summary.defragTime > lastViewedFeedMillis;

    return (
      compact([
        showDefragCard && hasNotSeenDefrag,
        showAutopilotSuggestionsCard && hasNotSeenDefrag,
      ]).length > 0
    );
  }, [lastViewedFeedMillis, org?.tailoredDefragCardSummary]);

  const onClick = () => {
    const status = flowState?.current?.state ?? "Unknown";
    track(TrackingEvents.DEFRAG.CLICK_AUTOPILOT_FEED_STATUS, { status, activeView });
    window.open(getUrlForFeature("flexibleMeetings"), webappDefaultWindowName);
  };

  const state =
    (data?.viewer.user &&
      currentTime.toMillis() - data.viewer.user.createdTime <= TWELVE_HOURS &&
      DefragRunFlowState.Unstarted) ||
    flowState?.current?.state;

  let body = null;
  if (!canUserAccessFlexMeetings) {
    return (
      <AutopilotStatusTrialExpired
        openPricingDialog={(open = true) => dispatch(setPricingDialogOpen(open))}
        orgId={orgId}
        orgTimeZone={timeZone}
      />
    );
  } else {
    switch (state) {
      case DefragRunFlowState.Unstarted: {
        const events = getValue(org?.autopilotEventsResponse);
        const meetingCount = events?.enabledAttending.concat(events.enabledOrganized).length;
        body = (
          <AutopilotStatusUnstarted
            onClick={onClick}
            meetingCount={meetingCount}
            timeZone={timeZone}
            badged={badged}
          />
        );
        break;
      }
      case DefragRunFlowState.Queued:
        body = (
          <AutopilotStatusCard
            title={t("autopilot-status:queued")}
            description="This may take a few minutes"
            onClick={onClick}
            muted
            badged={badged}
          />
        );
        break;
      case DefragRunFlowState.Running:
        body = (
          <AutopilotStatusCard
            title="Searching for better times..."
            description="This may take a few minutes"
            emphasis
            badged={badged}
          />
        );
        break;
      case DefragRunFlowState.Finished: {
        const error = getError(org?.tailoredDefragCardSummary);
        body = (
          <AutopilotStatusFinished
            onClick={onClick}
            flowState={flowState}
            nextDefragTime={getValue(org?.nextDefragTime)?.millis}
            meetingsMoved={getValue(org?.tailoredDefragCardSummary)?.yourEventMoves.length}
            defragError={!!error && error.statusCode !== 404}
            badged={badged}
          />
        );
        break;
      }
    }
  }

  if (!fastCrawlFinished) {
    return null;
  }

  if (loading) {
    return (
      <Card className="cw-mt-4">
        <Skeleton variant="text" height={20} />
      </Card>
    );
  } else {
    return (
      <div cw-id="autopilot-status" className="cw-select-none cw-relative">
        {body}
      </div>
    );
  }
}
